import { createFileLogger } from '../../../utils/logger';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import {
  Document, Paragraph, TextRun, HeadingLevel, Packer, AlignmentType, UnderlineType,
  ExternalHyperlink, Table, TableRow, TableCell, WidthType, ShadingType, BorderStyle,
  ImageRun,
} from 'docx';
import { customMarkdownSerializer } from './documentSerializers';

const log = createFileLogger('EXPORT_UTILS');

export interface ExportToWordOptions {
  documentTitle: string;
  prosemirrorDoc?: ProseMirrorNode;
}

export interface ExportToPDFOptions {
  documentTitle: string;
  prosemirrorDoc?: any;
}

export interface ExportToMarkdownOptions {
  documentTitle: string;
  editorState?: any;
}

export interface ExportToPlainTextOptions {
  documentTitle: string;
  editorState?: any;
}

const sanitizeFilename = (title: string): string =>
  title.replace(/[/\\:*?"<>|]/g, '').trim() || 'Document';

const triggerDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Defer revocation so the browser has time to begin the download
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

// ─── HTML serialization (for PDF print) ─────────────────────────────────────

const escapeHtml = (str: string): string => {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const nodeToHtml = (node: any): string => {
  if (!node) return '';

  switch (node.type) {
    case 'paragraph': {
      const content = node.content ? node.content.map(nodeToHtml).join('') : '';
      return `<p>${content || '<br>'}</p>`;
    }
    case 'heading': {
      const level = node.attrs?.level || 1;
      const content = node.content ? node.content.map(nodeToHtml).join('') : '';
      return `<h${level}>${content}</h${level}>`;
    }
    case 'blockquote': {
      const content = node.content ? node.content.map(nodeToHtml).join('') : '';
      return `<blockquote>${content}</blockquote>`;
    }
    case 'code_block': {
      const text = node.content ? node.content.map((c: any) => c.text || '').join('') : '';
      const lang = node.attrs?.language || '';
      return `<pre><code class="${lang}">${escapeHtml(text)}</code></pre>`;
    }
    case 'bullet_list': {
      const items = node.content ? node.content.map(nodeToHtml).join('') : '';
      return `<ul>${items}</ul>`;
    }
    case 'ordered_list': {
      const items = node.content ? node.content.map(nodeToHtml).join('') : '';
      const start = node.attrs?.order > 1 ? ` start="${node.attrs.order}"` : '';
      return `<ol${start}>${items}</ol>`;
    }
    case 'list_item': {
      const content = node.content ? node.content.map(nodeToHtml).join('') : '';
      return `<li>${content}</li>`;
    }
    case 'horizontal_rule':
      return '<hr>';
    case 'page_break':
      // In HTML/print context, a page break is rendered as a CSS page-break
      return '<div style="page-break-after: always; break-after: page; height: 1px; margin: 16px 0;"></div>';
    case 'footnote': {
      const num = node.attrs?.number || 1;
      return `<sup style="color:#3b82f6;font-size:0.7em;font-weight:600;" title="${escapeHtml(node.attrs?.content || '')}">[${num}]</sup>`;
    }
    case 'hard_break':
      return '<br>';
    case 'table': {
      const rows = node.content ? node.content.map(nodeToHtml).join('') : '';
      return `<table>${rows}</table>`;
    }
    case 'table_row': {
      const cells = node.content ? node.content.map(nodeToHtml).join('') : '';
      return `<tr>${cells}</tr>`;
    }
    case 'table_cell': {
      const content = node.content ? node.content.map(nodeToHtml).join('') : '';
      const bg = node.attrs?.background || node.attrs?.backgroundColor || '';
      const align = node.attrs?.textAlign || 'left';
      const colspan = node.attrs?.colspan > 1 ? ` colspan="${node.attrs.colspan}"` : '';
      const rowspan = node.attrs?.rowspan > 1 ? ` rowspan="${node.attrs.rowspan}"` : '';
      const style = `text-align:${align};${bg ? `background-color:${bg};` : ''}`;
      return `<td${colspan}${rowspan} style="${style}">${content}</td>`;
    }
    case 'table_header': {
      const content = node.content ? node.content.map(nodeToHtml).join('') : '';
      const bg = node.attrs?.background || node.attrs?.backgroundColor || '';
      const align = node.attrs?.textAlign || 'left';
      const colspan = node.attrs?.colspan > 1 ? ` colspan="${node.attrs.colspan}"` : '';
      const rowspan = node.attrs?.rowspan > 1 ? ` rowspan="${node.attrs.rowspan}"` : '';
      const style = `text-align:${align};${bg ? `background-color:${bg};` : ''}`;
      return `<th${colspan}${rowspan} style="${style}">${content}</th>`;
    }
    case 'text': {
      let text = escapeHtml(node.text || '');
      if (!node.marks) return text;

      let linkMark: any = null;
      const styleProps: string[] = [];

      node.marks.forEach((mark: any) => {
        switch (mark.type) {
          case 'strong':      text = `<strong>${text}</strong>`; break;
          case 'em':          text = `<em>${text}</em>`; break;
          case 'underline':   text = `<u>${text}</u>`; break;
          case 'strike':      text = `<s>${text}</s>`; break;
          case 'code':        text = `<code>${text}</code>`; break;
          case 'subscript':   text = `<sub>${text}</sub>`; break;
          case 'superscript': text = `<sup>${text}</sup>`; break;
          case 'highlight':
            styleProps.push('background-color:#FFFF00');
            break;
          case 'smallCaps':
            styleProps.push('font-variant:small-caps');
            break;
          case 'letterSpacing':
            if (mark.attrs?.value != null) styleProps.push(`letter-spacing:${mark.attrs.value}em`);
            break;
          case 'textTransform':
            if (mark.attrs?.transform) styleProps.push(`text-transform:${mark.attrs.transform}`);
            break;
          case 'fontSize':
            if (mark.attrs?.size) styleProps.push(`font-size:${mark.attrs.size}pt`);
            break;
          case 'fontFamily':
            if (mark.attrs?.font) styleProps.push(`font-family:'${mark.attrs.font}'`);
            break;
          case 'fontWeight':
            if (mark.attrs?.weight) styleProps.push(`font-weight:${mark.attrs.weight}`);
            break;
          case 'textColor':
            if (mark.attrs?.color) styleProps.push(`color:${mark.attrs.color}`);
            break;
          case 'backgroundColor':
            if (mark.attrs?.color) styleProps.push(`background-color:${mark.attrs.color}`);
            break;
          case 'link':
            linkMark = mark;
            break;
          // Strip track-changes and metadata marks — they must not appear in the exported file
          case 'deletion':
            // Deleted text should not appear in the export at all
            text = '';
            break;
          case 'insertion':
          case 'comment':
          case 'ai_detected':
            break;
        }
      });

      if (!text) return '';
      if (styleProps.length > 0) {
        text = `<span style="${styleProps.join(';')}">${text}</span>`;
      }
      if (linkMark) {
        text = `<a href="${escapeHtml(linkMark.attrs?.href || '')}">${text}</a>`;
      }
      return text;
    }
    case 'image': {
      const src = node.attrs?.src || '';
      const alt = escapeHtml(node.attrs?.alt || '');
      const width = node.attrs?.width ? ` width="${node.attrs.width}"` : '';
      const align = node.attrs?.alignment || 'left';
      const style = align === 'center' ? 'display:block;margin:0 auto;' : align === 'right' ? 'float:right;' : '';
      return `<figure style="margin:16px 0;"><img src="${escapeHtml(src)}" alt="${alt}"${width} style="max-width:100%;${style}" /></figure>`;
    }
    case 'math_inline': {
      const latex = escapeHtml(node.attrs?.latex || node.attrs?.content || '');
      return `<code class="math-inline">\\(${latex}\\)</code>`;
    }
    case 'math_display': {
      const latex = escapeHtml(node.attrs?.latex || node.attrs?.content || '');
      return `<div class="math-display" style="text-align:center;margin:16px 0;">\\[${latex}\\]</div>`;
    }
    default:
      return node.content ? node.content.map(nodeToHtml).join('') : '';
  }
};

// ─── DOCX serialization ──────────────────────────────────────────────────────

const PX_TO_EMU = 9525; // 1 CSS pixel at 96 DPI = 9525 EMU
const MAX_IMAGE_WIDTH_PX = 500;

/**
 * Fetch an image URL and return its data + rendered dimensions for DOCX embedding.
 * Returns null if fetching fails, so the caller can fall back gracefully.
 */
const fetchImageForDocx = async (
  src: string,
  storedWidth: number | null,
  storedHeight: number | null,
): Promise<{ data: ArrayBuffer; width: number; height: number } | null> => {
  try {
    const response = await fetch(src);
    if (!response.ok) return null;
    const data = await response.arrayBuffer();

    // Use stored dimensions if available, otherwise detect from the image element
    let w = storedWidth || 0;
    let h = storedHeight || 0;

    if (!w || !h) {
      const dims = await new Promise<{ w: number; h: number }>((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve({ w: 400, h: 300 });
        img.src = src;
      });
      w = dims.w || 400;
      h = dims.h || 300;
    }

    // Scale down to fit MAX_IMAGE_WIDTH_PX, preserving aspect ratio
    if (w > MAX_IMAGE_WIDTH_PX) {
      h = Math.round((h * MAX_IMAGE_WIDTH_PX) / w);
      w = MAX_IMAGE_WIDTH_PX;
    }

    return { data, width: w * PX_TO_EMU, height: h * PX_TO_EMU };
  } catch {
    return null;
  }
};

/**
 * Build an array of TextRun / ExternalHyperlink from a ProseMirror inline content node.
 */
const buildTextRuns = (contentNode: ProseMirrorNode): any[] => {
  const runs: any[] = [];

  contentNode.forEach((node) => {
    // Hard break → line break in TextRun
    if (node.type.name === 'hard_break') {
      runs.push(new TextRun({ break: 1 }));
      return;
    }

    // Inline atoms (footnote, math_inline) → plain-text representation
    if (!node.isText) {
      if (node.type.name === 'footnote') {
        runs.push(new TextRun({ text: `[${node.attrs?.number ?? ''}]`, superScript: true }));
      } else if (node.type.name === 'math_inline') {
        runs.push(new TextRun({ text: node.attrs?.latex || '', font: { name: 'Cambria Math' } }));
      }
      return;
    }

    const text = node.text || '';
    const marks = node.marks || [];
    const runProps: any = { text };
    let linkMark: any = null;

    marks.forEach((mark) => {
      switch (mark.type.name) {
        case 'strong':      runProps.bold = true; break;
        case 'em':          runProps.italics = true; break;
        case 'underline':   runProps.underline = { type: UnderlineType.SINGLE }; break;
        case 'strike':      runProps.strike = true; break;
        case 'code':        runProps.font = { name: 'Courier New' }; break;
        case 'subscript':   runProps.subScript = true; break;
        case 'superscript': runProps.superScript = true; break;
        case 'smallCaps':   runProps.smallCaps = true; break;
        case 'textColor': {
          const color = (mark.attrs?.color || '').replace('#', '');
          if (/^[0-9a-fA-F]{6}$/.test(color)) runProps.color = color;
          break;
        }
        case 'backgroundColor': {
          const color = (mark.attrs?.color || '').replace('#', '');
          if (/^[0-9a-fA-F]{6}$/.test(color)) runProps.shading = { fill: color, type: ShadingType.CLEAR, color: 'auto' };
          break;
        }
        case 'highlight':
          runProps.shading = { fill: 'FFFF00', type: ShadingType.CLEAR, color: 'auto' };
          break;
        case 'fontWeight': {
          const w = parseInt(mark.attrs?.weight || '400', 10);
          if (w >= 600) runProps.bold = true;
          break;
        }
        case 'fontSize':
          if (mark.attrs?.size) runProps.size = Math.round(parseFloat(mark.attrs.size) * 2);
          break;
        case 'fontFamily':
          if (mark.attrs?.font) runProps.font = { name: mark.attrs.font };
          break;
        case 'link':
          linkMark = mark;
          break;
        // Ignore metadata/tracking marks in the exported file
        case 'deletion':
        case 'insertion':
        case 'comment':
        case 'ai_detected':
          break;
      }
    });

    if (linkMark) {
      runs.push(new ExternalHyperlink({
        link: linkMark.attrs?.href || '',
        children: [new TextRun({ ...runProps, style: 'Hyperlink' })],
      }));
    } else {
      runs.push(new TextRun(runProps));
    }
  });

  return runs.length > 0 ? runs : [new TextRun('')];
};

/**
 * Recursively build DOCX Paragraph nodes from a list node (handles nesting).
 */
const buildListParagraphs = (listNode: ProseMirrorNode, isOrdered: boolean, level = 0): any[] => {
  const result: any[] = [];

  listNode.forEach((listItem) => {
    listItem.forEach((itemChild) => {
      if (itemChild.type.name === 'paragraph') {
        const runs = buildTextRuns(itemChild);
        result.push(
          isOrdered
            ? new Paragraph({ children: runs, numbering: { reference: 'default-numbering', level } })
            : new Paragraph({ children: runs, bullet: { level } })
        );
      } else if (itemChild.type.name === 'bullet_list') {
        result.push(...buildListParagraphs(itemChild, false, level + 1));
      } else if (itemChild.type.name === 'ordered_list') {
        result.push(...buildListParagraphs(itemChild, true, level + 1));
      }
      // Other block types inside list items (code blocks, etc.) are serialized as plain text
      else {
        const text = itemChild.textContent;
        if (text) {
          result.push(
            isOrdered
              ? new Paragraph({ children: [new TextRun(text)], numbering: { reference: 'default-numbering', level } })
              : new Paragraph({ children: [new TextRun(text)], bullet: { level } })
          );
        }
      }
    });
  });

  return result;
};

/**
 * Build block-level DOCX content from any ProseMirror block node.
 * Used recursively (e.g. inside blockquotes) and for top-level document content.
 */
const buildBlockContent = async (blockNode: ProseMirrorNode, indentLeft = 0): Promise<any[]> => {
  const content: any[] = [];

  const headingLevelMap: Record<number, typeof HeadingLevel[keyof typeof HeadingLevel]> = {
    1: HeadingLevel.HEADING_1, 2: HeadingLevel.HEADING_2, 3: HeadingLevel.HEADING_3,
    4: HeadingLevel.HEADING_4, 5: HeadingLevel.HEADING_5, 6: HeadingLevel.HEADING_6,
  };

  const paragraphOpts = (extra: object = {}) => indentLeft ? { indent: { left: indentLeft }, ...extra } : extra;

  const pushes = [] as Array<() => Promise<void>>;

  blockNode.forEach((child) => {
    pushes.push(async () => {
      switch (child.type.name) {
        case 'paragraph': {
          content.push(new Paragraph({ children: buildTextRuns(child), ...paragraphOpts() }));
          break;
        }

        case 'heading': {
          const level = child.attrs?.level || 1;
          content.push(new Paragraph({
            heading: headingLevelMap[level] ?? HeadingLevel.HEADING_1,
            children: buildTextRuns(child),
            ...paragraphOpts(),
          }));
          break;
        }

        case 'blockquote': {
          // Recurse with increased indent (720 twips = 0.5 inch)
          const inner = await buildBlockContent(child, indentLeft + 720);
          content.push(...inner);
          break;
        }

        case 'code_block': {
          // Split code text on newlines; each line becomes a TextRun with break after it
          const rawText = child.textContent || '';
          const lines = rawText.split('\n');
          const runs: any[] = [];
          lines.forEach((line, i) => {
            runs.push(new TextRun({ text: line, font: { name: 'Courier New' } }));
            if (i < lines.length - 1) runs.push(new TextRun({ break: 1 }));
          });
          content.push(new Paragraph({
            children: runs.length > 0 ? runs : [new TextRun('')],
            shading: { fill: 'F5F5F5', type: ShadingType.CLEAR, color: 'auto' },
            ...paragraphOpts(),
          }));
          break;
        }

        case 'bullet_list':
          content.push(...buildListParagraphs(child, false, 0));
          break;

        case 'ordered_list':
          content.push(...buildListParagraphs(child, true, 0));
          break;

        case 'horizontal_rule':
          content.push(new Paragraph({
            children: [new TextRun('')],
            border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: '000000', space: 1 } },
          }));
          break;

        case 'page_break':
          content.push(new Paragraph({ children: [new TextRun({ break: 1 })], pageBreakBefore: true }));
          break;

        case 'image': {
          const { src, alt, width, height } = child.attrs || {};
          if (src) {
            const imageData = await fetchImageForDocx(src, width, height);
            if (imageData) {
              content.push(new Paragraph({
                children: [new ImageRun({ type: 'png', data: imageData.data, transformation: { width: imageData.width, height: imageData.height } })],
              }));
            } else {
              // Fallback: text placeholder when the image cannot be fetched
              const label = alt ? `[Image: ${alt}]` : '[Image]';
              content.push(new Paragraph({ children: [new TextRun({ text: label, italics: true })] }));
            }
          }
          break;
        }

        case 'math_display': {
          const latex = child.attrs?.latex || '';
          content.push(new Paragraph({
            children: [new TextRun({ text: latex, font: { name: 'Cambria Math' } })],
            ...paragraphOpts(),
          }));
          break;
        }

        case 'table': {
          const rows: any[] = [];
          child.forEach((rowNode) => {
            if (rowNode.type.name !== 'table_row') return;
            const cells: any[] = [];
            rowNode.forEach((cellNode) => {
              const isHeader = cellNode.type.name === 'table_header';
              const cellParagraphs: any[] = [];
              cellNode.forEach((cellChild) => {
                if (cellChild.type.name === 'paragraph') {
                  cellParagraphs.push(new Paragraph({ children: buildTextRuns(cellChild) }));
                } else {
                  // Other block content in cells: serialize as plain text
                  const text = cellChild.textContent;
                  if (text) cellParagraphs.push(new Paragraph({ children: [new TextRun(text)] }));
                }
              });
              cells.push(new TableCell({
                children: cellParagraphs.length > 0 ? cellParagraphs : [new Paragraph({ children: [new TextRun('')] })],
                shading: isHeader ? { fill: 'F0F0F0', type: ShadingType.CLEAR, color: 'auto' } : undefined,
              }));
            });
            rows.push(new TableRow({ children: cells }));
          });
          if (rows.length > 0) {
            content.push(new Table({ rows, width: { size: 100, type: WidthType.PERCENTAGE } }));
          }
          break;
        }

        default: {
          const text = child.textContent;
          if (text) content.push(new Paragraph({ children: [new TextRun(text)] }));
          break;
        }
      }
    });
  });

  for (const push of pushes) await push();
  return content;
};

/**
 * Convert the top-level ProseMirror document node into DOCX content blocks.
 */
const pmDocToDocxContent = async (pmNode: ProseMirrorNode): Promise<any[]> => {
  return buildBlockContent(pmNode);
};

// ─── Public export functions ─────────────────────────────────────────────────

export const exportToWord = async ({ documentTitle, prosemirrorDoc }: ExportToWordOptions): Promise<void> => {
  try {
    log.log('[ExportUtils] Starting Word export');

    if (!(prosemirrorDoc instanceof ProseMirrorNode)) {
      throw new Error('No content to export. Please ensure the document is loaded.');
    }

    const docContent = await pmDocToDocxContent(prosemirrorDoc);

    const wordDocument = new Document({
      sections: [{ properties: {}, children: docContent }],
      numbering: {
        config: [{
          reference: 'default-numbering',
          levels: Array.from({ length: 9 }, (_, level) => ({
            level,
            format: 'decimal',
            text: `%${level + 1}.`,
            alignment: AlignmentType.LEFT,
          })),
        }],
      },
    });

    const blob = await Packer.toBlob(wordDocument);
    triggerDownload(blob, `${sanitizeFilename(documentTitle)}.docx`);

    log.log('[ExportUtils] Word export complete');
  } catch (error) {
    log.error('[ExportUtils] Error exporting to Word:', error);
    throw error;
  }
};

export const exportToPDF = async ({ documentTitle, prosemirrorDoc }: ExportToPDFOptions): Promise<void> => {
  const cleanTitle = sanitizeFilename(documentTitle);

  const htmlContent = prosemirrorDoc?.content
    ? prosemirrorDoc.content.map((node: any) => nodeToHtml(node)).join('')
    : '<p>No content available</p>';

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    throw new Error('Could not open print window. Please allow popups for this site.');
  }

  printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${cleanTitle}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; line-height: 1.5; color: #000; padding: 0; }
    h1 { font-size: 24pt; font-weight: bold; margin: 16pt 0 8pt; }
    h2 { font-size: 18pt; font-weight: bold; margin: 14pt 0 8pt; }
    h3 { font-size: 14pt; font-weight: bold; margin: 12pt 0 6pt; }
    h4, h5, h6 { font-size: 12pt; font-weight: bold; margin: 10pt 0 5pt; }
    p { margin: 0 0 8pt; }
    ul, ol { margin: 0 0 8pt; padding-left: 24pt; }
    li { margin-bottom: 3pt; }
    blockquote { border-left: 3pt solid #ccc; padding-left: 12pt; margin: 8pt 0; color: #555; }
    pre { background: #f5f5f5; border: 1pt solid #ddd; border-radius: 3pt; padding: 8pt; margin: 8pt 0; font-family: 'Courier New', monospace; font-size: 9pt; white-space: pre-wrap; word-break: break-word; }
    code { font-family: 'Courier New', monospace; background: #f5f5f5; padding: 1pt 3pt; border-radius: 2pt; font-size: 9pt; }
    table { border-collapse: collapse; width: 100%; margin: 8pt 0; }
    th, td { border: 1pt solid #000; padding: 4pt 8pt; text-align: left; vertical-align: top; }
    th { background-color: #f0f0f0; font-weight: bold; }
    hr { border: none; border-top: 1pt solid #000; margin: 12pt 0; }
    a { color: #1155CC; text-decoration: underline; }
    @page { margin: 1in; }
  </style>
</head>
<body>${htmlContent}</body>
</html>`);

  // Register onload before document.close() to avoid the race where close() fires
  // the load event synchronously (inline document with no external resources).
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
  printWindow.addEventListener('afterprint', () => printWindow.close());

  printWindow.document.close();
};

export const exportToMarkdown = async ({ documentTitle, editorState }: ExportToMarkdownOptions): Promise<void> => {
  try {
    if (!editorState?.doc) {
      throw new Error('No document content available for Markdown export.');
    }

    const markdown = customMarkdownSerializer.serialize(editorState.doc);
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    triggerDownload(blob, `${sanitizeFilename(documentTitle)}.md`);

    log.log('[ExportUtils] Markdown export complete');
  } catch (error) {
    log.error('[ExportUtils] Error exporting to Markdown:', error);
    throw error;
  }
};

export const exportToPlainText = async ({ documentTitle, editorState }: ExportToPlainTextOptions): Promise<void> => {
  try {
    if (!editorState?.doc) {
      throw new Error('No document content available for plain text export.');
    }

    const text = editorState.doc.textBetween(0, editorState.doc.content.size, '\n\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    triggerDownload(blob, `${sanitizeFilename(documentTitle)}.txt`);

    log.log('[ExportUtils] Plain text export complete');
  } catch (error) {
    log.error('[ExportUtils] Error exporting to plain text:', error);
    throw error;
  }
};
